// One product row inside a calculator's computed result ("You need 2 bags" +
// the product), shared by every /tools route that carries one. Pure: sizing,
// the demote guard, and the tracking surface name live here so the component
// and the tests read one source.

import { extractAsin } from '@/lib/product-picks/resolve'
import { wholeContainers } from '@/lib/units/whole-containers'

/**
 * ASINs the Q3-2026 Amazon EPC ladder demoted (obsidian affiliate-audit-2026-09
 * README section 3). A new surface never shows one; the caller renders no
 * product rather than a demoted one.
 */
export const DEMOTED_ASINS: ReadonlySet<string> = new Set([
  'B0DYWRC5NM', // Pennington 32-0-5
  'B07YM583XL', // Ortho WeedClear RTS
  'B07K78HDDS', // Spectracide Weed Stop
  'B07SGL71PP', // Jacklin Heisman KBG
  'B0976DN86K', // St. Augustine Palmetto plugs
  'B0053NDZJW', // Southern Ag Chelated Iron
  'B0030MIHAU', // Fiskars weed puller
  'B09JVFT7N5', // Scotts Triple Action
])

/** Scotts Halts pre-emergent: only earns in its Feb-Apr window. */
export const SEASONAL_ASINS: Readonly<Record<string, readonly number[]>> = {
  B08L6T3B4H: [1, 2, 3],
}

export function isDemotedAsin(asin: string | null | undefined, now: Date = new Date()): boolean {
  if (!asin) return false
  const key = asin.toUpperCase()
  if (DEMOTED_ASINS.has(key)) return true
  const months = SEASONAL_ASINS[key]
  return months ? !months.includes(now.getUTCMonth()) : false
}

/** Tracking surface, stamped as `surface` and so as `placement_key`. */
export function toolResultSurface(toolSlug: string): string {
  return `tool-result:${toolSlug}`
}

/**
 * Bag weight in lb read from a listing name: "(7 lb)", "5 lb", "15-lb",
 * "10 lbs", "1 lb, covers ...". Null when the name carries no weight.
 */
export function parseBagLbs(name: string): number | null {
  const m = name.match(/(\d+(?:\.\d+)?)\s*-?\s*(?:lb|lbs|pound|pounds)\b/i)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * High end of a preformatted pound range ("3.5-5 lb", "1,200-1,800 lb"), so a
 * bag count sized from it never leaves the reader short. Null when no number.
 */
export function upperBoundLbs(range: string | null | undefined): number | null {
  if (!range) return null
  const nums = range.replace(/,/g, '').match(/\d+(?:\.\d+)?/g)
  if (!nums) return null
  const n = Number(nums[nums.length - 1])
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Whole bags to cover `totalLbs`. Null when either input is unusable. */
export function bagsNeeded(totalLbs: number | null | undefined, bagLbs: number | null | undefined): number | null {
  if (!totalLbs || !bagLbs || !Number.isFinite(totalLbs) || !Number.isFinite(bagLbs)) return null
  if (totalLbs <= 0 || bagLbs <= 0) return null
  return Math.max(1, wholeContainers(totalLbs, bagLbs))
}

function formatLbs(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)
}

export function formatBagLine(bags: number, bagLbs: number): string {
  return `You need ${bags} × ${formatLbs(bagLbs)} lb ${bags === 1 ? 'bag' : 'bags'}`
}

export interface ToolResultPickInput {
  name: string
  affiliateUrl: string | null | undefined
  asin?: string | null
  imageUrl?: string | null
  reason: string
}

export interface ToolResultPickData {
  surface: string
  toolSlug: string
  name: string
  affiliateUrl: string
  asin: string | null
  imageUrl: string | null
  reason: string
  /** "You need 2 × 7 lb bags", or null when the result carries no amount. */
  quantityLine: string | null
  /** 1-based slot in the result. One row per result, so always 1 today. */
  position: number
}

/**
 * Resolve the single in-result pick. Null when there is no link or the ASIN is
 * demoted, so the result renders without a product instead of a bad one.
 * `bagLbs` defaults to the weight in the product name.
 */
export function buildToolResultPick(opts: {
  toolSlug: string
  product: ToolResultPickInput | null | undefined
  totalLbs?: number | null
  bagLbs?: number | null
  now?: Date
}): ToolResultPickData | null {
  const { toolSlug, product } = opts
  if (!product || !product.affiliateUrl) return null
  const asin = (product.asin ?? extractAsin(product.affiliateUrl))?.toUpperCase() ?? null
  if (isDemotedAsin(asin, opts.now)) return null

  const bagLbs = opts.bagLbs ?? parseBagLbs(product.name)
  const bags = bagsNeeded(opts.totalLbs, bagLbs)

  return {
    surface: toolResultSurface(toolSlug),
    toolSlug,
    name: product.name,
    affiliateUrl: product.affiliateUrl,
    asin,
    imageUrl: product.imageUrl ?? null,
    reason: product.reason,
    quantityLine: bags && bagLbs ? formatBagLine(bags, bagLbs) : null,
    position: 1,
  }
}
